// Period Helper Utilities - SAT V4 Zone
import type { CurriculumSubject, Subject, SubjectPeriods, Teacher } from '../../types';
import type { WeekType } from '../components/WeekToggle';

export type ScheduleType = 'Weekly' | 'Bi-Weekly';

/**
 * Returns the appropriate column header based on schedule type
 */
export const getPeriodsColumnHeader = (_scheduleType: ScheduleType): string => {
  return 'Periods';
};

/**
 * Returns the appropriate total periods header based on schedule type
 */
export const getTotalPeriodsHeader = (scheduleType: ScheduleType): string => {
  return scheduleType === 'Bi-Weekly' ? 'Total Periods (Bi-Weekly)' : 'Total Periods';
};

/**
 * Checks if a value is a SubjectPeriods object
 */
const isSubjectPeriods = (value: number | SubjectPeriods): value is SubjectPeriods => {
  return typeof value === 'object' && value !== null && 'weekly' in value;
};

/**
 * Gets the period value for a subject based on schedule type and selected week
 */
export const getSubjectPeriodsValue = (
  subject: Subject,
  yearGroup: number,
  scheduleType: ScheduleType,
  selectedWeek?: WeekType,
): number => {
  const periods = subject.periodsPerWeek[yearGroup];

  if (!periods) return 0;

  if (isSubjectPeriods(periods)) {
    if (scheduleType === 'Bi-Weekly' && selectedWeek) {
      return selectedWeek === 'A' ? (periods.weekA ?? periods.weekly) : (periods.weekB ?? periods.weekly);
    }
    return periods.weekly;
  }

  // For backward compatibility with simple number values
  return periods;
};

/**
 * Gets the total periods for a subject across both weeks (for Bi-Weekly) or single week (for Weekly)
 */
export const getSubjectTotalPeriods = (subject: Subject, yearGroup: number, scheduleType: ScheduleType): number => {
  const periods = subject.periodsPerWeek[yearGroup];

  if (!periods) return 0;

  if (isSubjectPeriods(periods)) {
    if (scheduleType === 'Bi-Weekly') {
      const weekA = periods.weekA ?? periods.weekly;
      const weekB = periods.weekB ?? periods.weekly;
      return weekA + weekB;
    }
    return periods.weekly;
  }

  // For backward compatibility - if Bi-Weekly, double the weekly value
  return scheduleType === 'Bi-Weekly' ? periods * 2 : periods;
};

/**
 * Gets curriculum subject periods based on schedule type and week
 */
export const getCurriculumPeriodsValue = (
  curriculumSubject: CurriculumSubject,
  scheduleType: ScheduleType,
  selectedWeek?: WeekType,
): number => {
  if (scheduleType === 'Bi-Weekly' && selectedWeek) {
    return selectedWeek === 'A'
      ? (curriculumSubject.periodsWeekA ?? curriculumSubject.periodsPerWeek)
      : (curriculumSubject.periodsWeekB ?? curriculumSubject.periodsPerWeek);
  }
  return curriculumSubject.periodsPerWeek;
};

/**
 * Gets total curriculum subject periods (for Bi-Weekly: sum of both weeks)
 */
export const getCurriculumTotalPeriods = (curriculumSubject: CurriculumSubject, scheduleType: ScheduleType): number => {
  if (scheduleType === 'Bi-Weekly') {
    const weekA = curriculumSubject.periodsWeekA ?? curriculumSubject.periodsPerWeek;
    const weekB = curriculumSubject.periodsWeekB ?? curriculumSubject.periodsPerWeek;
    return weekA + weekB;
  }
  return curriculumSubject.periodsPerWeek;
};

/**
 * Gets teacher's max periods based on schedule type and week
 */
export const getTeacherMaxPeriods = (teacher: Teacher, scheduleType: ScheduleType, selectedWeek?: WeekType): number => {
  if (scheduleType === 'Bi-Weekly' && selectedWeek) {
    return selectedWeek === 'A'
      ? (teacher.maxPeriodsWeekA ?? teacher.maxPeriodsPerWeek)
      : (teacher.maxPeriodsWeekB ?? teacher.maxPeriodsPerWeek);
  }
  return teacher.maxPeriodsPerWeek;
};

/**
 * Gets teacher's current assigned periods based on schedule type and week
 */
export const getTeacherCurrentPeriods = (
  teacher: Teacher,
  scheduleType: ScheduleType,
  selectedWeek?: WeekType,
): number => {
  if (scheduleType === 'Bi-Weekly' && selectedWeek) {
    return selectedWeek === 'A'
      ? (teacher.currentPeriodsWeekA ?? teacher.currentPeriodsAssigned)
      : (teacher.currentPeriodsWeekB ?? teacher.currentPeriodsAssigned);
  }
  return teacher.currentPeriodsAssigned;
};

/**
 * Formats period display with bi-weekly context
 */
export const formatPeriodsDisplay = (periods: number, scheduleType: ScheduleType, selectedWeek?: WeekType): string => {
  if (scheduleType === 'Bi-Weekly' && selectedWeek) {
    return `${periods} (Week ${selectedWeek})`;
  }
  return `${periods}`;
};
